import api from "./interceptor";
import config from "../config";
import { CryptoMethods } from "../utils/crypto-methods";

// Creating Training Session APIs

export const getTrainers = async (trainerType: string, search: string) => {
  try {
    const response = await api.get(config.userEndPoints.trainers, {
      params: { trainerType, search, pageSize: 50 },
    });
    let trainers = response.data?.data?.trainers || [];
    trainers = await Promise.all(
      trainers.map(async (trainer: any) => ({
        ...trainer,
        firstName: await CryptoMethods.ecbDecryptData(trainer.firstName),
        lastName: await CryptoMethods.ecbDecryptData(trainer.lastName),
        email: await CryptoMethods.ecbDecryptData(trainer.email),
      }))
    );
    return trainers;
  } catch (error) {
    console.error("Error fetching trainers:", error);
    return [];
  }
};

export const getOrganisation = async (
  collabOrg: number,
  search: string,
  orderBy?: string,
  sortBy?: string
) => {
  try {
    const params: any = {
      collabOrg,
      name: search,
      pageSize: 50,
    };

    if (orderBy) params.orderBy = orderBy;
    if (sortBy) params.sortBy = sortBy;

    const response = await api.get(config.trainingEndPoints.organizations, {
      params,
    });
    return response.data.data.organisations;
  } catch (error) {
    console.error("Error fetching organisations:", error);
    return error;
  }
};

export const addClassDetail = async (organisationPayload: any) => {
  try {
    const response = await api.post(
      config.trainingEndPoints.trainingSessions,
      organisationPayload
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export const uploadDocument = async (
  file: File,
  documentType: string,
  sessionId: number,
  instanceId?: number
) => {
  try {
    const formData = new FormData();
    formData.append("sessionId", sessionId + "");
    formData.append("file", file);
    formData.append("documentType", documentType);
    if (instanceId) {
      formData.append("trainingInstanceId", instanceId + "");
    }

    const response = await api.post(
      config.trainingEndPoints.documentUpload,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Tenant-Id": "1",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to upload document:", error);
    throw error;
  }
};

export const sessionStatusUpdate = async (sessionId: any) => {
  try {
    const response = await api.patch(
      `${config.trainingEndPoints.trainingUpdateRequest}/${sessionId}`,
      {}
    );
    return response.data;
  } catch (error) {
    console.error("Error updating status:", error);
    return error;
  }
};

export const getDownloadTemplateUrl = async () => {
  try {
    const response = await api.get(
      config.trainingEndPoints.downloadStudentTemplate
    );
    if (response.status === 200) {
      window.open(response.data.data.studentTemplateURL, "_self");
    }
  } catch (error) {
    return error;
  }
};

export const uploadStudents = async (
  file: File,
  sessionId: number,
  instanceId?: number,
) => {
  try {
    const formData = new FormData();
    formData.append("trainingSessionId", sessionId + "");
    formData.append("file", file);
    if (instanceId) {
      formData.append("trainingInstanceId", instanceId + "");
    }

    const response = await api.post(
      config.trainingEndPoints.studentsUpload,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Tenant-Id": "1",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to upload document:", error);
    return error;
  }
};

export const getStudentsUploadHistoryList = async (
  sessionId: number,
  pageNumber: number,
  pageSize: number,
  instanceId?: number
) => {
  try {
    let params: Record<string, number> = {
      trainingSessionId: sessionId,
      pageNumber,
      pageSize,
    };
    if (instanceId) {
      params.trainingInstanceId = instanceId;
    }
    const response: any = await api.get(
      config.trainingEndPoints.studentsUploadHistory,
      { params }
    );
    return {
      uploads: response.data.data.studentsFileHistory,
      pagination: response.data._pagination
    };
  } catch (error) {
    return error;
  }
};

// Listing Training Session APIs

export interface ListTrainingSessionParams {
  status: any;
  trainingType: any;
  sponsoringOrganizationID: any;
  isHybrid?: boolean;
  pageNumber: number;
  pageSize: number;
  pageOrder: string;
  search?: string;
}

export const listTrainingSession = async ({
  status,
  trainingType,
  sponsoringOrganizationID,
  isHybrid,
  pageNumber,
  pageSize,
  pageOrder,
  search
}: ListTrainingSessionParams) => {
  try {
    const params: any = {
      status,
      trainingType,
      sponsoringOrganizationID,
      pageNumber,
      pageSize,
      pageOrder,
      search
    };

    if (isHybrid !== undefined) {
      params.isHybrid = isHybrid;
    }

    if (search) {
      params.search = search;
    }

    const response = await api.get(
      `${config.trainingEndPoints.trainingSessions}`,
      { params }
    );
    return response.data;
  } catch (err) {
    return err;
  }
};

// Editing Training Session APIs

export const fetchTrainingSessionById = async (id: number) => {
  try {
    const response = await api.get(
      `${config.trainingEndPoints.trainingSessions}/${id}`
    );
    let trainerList = response.data?.data?.trainingSession?.trainingSessionTrainer || [];
    trainerList = await Promise.all(
      trainerList.map(async (trainer: any) => ({
        ...trainer,
        firstName: await CryptoMethods.ecbDecryptData(trainer.firstName),
        lastName: await CryptoMethods.ecbDecryptData(trainer.lastName),
        email: await CryptoMethods.ecbDecryptData(trainer.email),
      }))
    );
    return {
      ...response.data,
      data: {
        ...response.data.data,
        trainingSession: {
          ...response.data.data.trainingSession,
          trainingSessionTrainer: trainerList,
        }
      },
    };
  } catch (error) {
    return error;
  }
};

export const getDocumentList = async (
  sessionId: number,
  documentType: string,
  instanceId?: number
) => {
  try {
    let params: any = { trainingSessionId: sessionId, documentType };
    if (instanceId) {
      params.trainingInstanceId = instanceId;
    }
    const response = await api.get(config.trainingEndPoints.getDocumentList, {
      params
    });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getStudentList = async (
  sessionId: number,
  pageNumber: number,
  pageSize: number,
  instanceId?: number,
) => {
  try {
    let params: Record<string, number> = {
      trainingSessionId: sessionId,
      pageNumber,
      pageSize,
    };
    if (instanceId) {
      params.trainingInstanceId = instanceId;
    }

    const response = await api.get(config.trainingEndPoints.students, {
      params,
    });
    let studentList = response.data?.data?.students || [];
    studentList = await Promise.all(
      studentList.map(async (student: any) => ({
        ...student,
        firstName: await CryptoMethods.ecbDecryptData(student.firstName),
        lastName: await CryptoMethods.ecbDecryptData(student.lastName),
        email: await CryptoMethods.ecbDecryptData(student.email),
      }))
    );
    return {
      students: studentList,
      pagination: response.data._pagination,
    };
  } catch (error) {
    return error;
  }
};

export const updateTrainingSession = async (sessionId: number, organisationPayload: any) => {
  try {
    const response = await api.put(
      `${config.trainingEndPoints.trainingSessions}/${sessionId}`,
      organisationPayload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating training session:", error);
    return null;
  }
};

export const deleteStudent = async (
  studentId: number,
  trainingSessionId: number,
  trainingInstanceId?: number,
) => {
  const deleteUrl = `${config.trainingEndPoints.student}/${studentId}?trainingSessionId=${trainingSessionId}&trainingInstanceId=${trainingInstanceId}`;

  try {
    const response = await api.delete(deleteUrl);
    return response.data; 
  } catch (error) {
    console.error('Error deleting student:', error);
    return error;
  }
};

export const downloadUploadedDocument = async (
  trainingSessionId: number,
  documentId: number,
  instanceId?: number,
  documentType?: string
) => {
  try {
    let requestBody: any = {
      trainingSessionId,
      documentId,
    };
    if (instanceId) {
      requestBody.trainingInstanceId = instanceId;
    }
    if (documentType) {
      requestBody.documentType = documentType;
    }
    const response = await api.post(config.trainingEndPoints.downloadUploadDocument,
      requestBody
    );
    if (response.status === 200) {
      const presignedUrl = response.data.data.sessionTrainer.presignedUrl;
      window.open(presignedUrl, "_self"); 
    }
  } catch (error) {
    console.error("Error downloading document:", error);
    return error;
  }
};

export const updateTrainingRequest = async (
  sessionId: number,
  status: string,
  remarks?: string
) => {
  try {
    let requestBody: Record<string, string> = {
      status,
    };
    if (remarks) {
      requestBody.remarks = remarks;
    }
    const response = await api.patch(
      `${config.trainingEndPoints.trainingStatus}${sessionId}`,
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error("Error updating training request:", error);
    return error;
  }
};

export const finalizeRoster = async (id: number) => {
  try {
    const response = await api.put(
      `${config.trainingEndPoints.finalizeRoster}/${id}`
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export const generateStudentCertificate = async (
  studentId: number[],
  instanceId: number,
  certificateType: string
) => {
  try {
    let requestBody: any = {
      userId: studentId,
      trainingInstanceId: instanceId,
      certificateType,
    };
    const response = await api.post(
      config.trainingEndPoints.addStudentCertificate,
      requestBody
    );
    if (response.status === 200) {
      const presignedUrl =
        response.data.data.studentCertificateDetails;
      window.open(presignedUrl, "_self");
    }
    return response;
  } catch (error) {
    return error;
  }
};

export const approveFeedBackDocument = async (
  documentId: number,
  sessionId: number,
  instanceId: number,
  payload: any
) => {
  try {
    const response = await api.put(
      `${config.trainingEndPoints.approveFeedBackDocument}/${documentId}`,
      payload,
      {
        params: {
          trainingSessionId: sessionId,
          trainingInstanceId: instanceId,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error approving the feedback: ", error);
    return error;
  }
};

export const deleteTrainingSession = async (sessionId: number) => {
  const deleteSession = `${config.trainingEndPoints.trainingSessions}/${sessionId}`;
  try {
    const response = await api.delete(deleteSession);
    return response.data;
  } catch (error) {
    console.error("Error deleting student:", error);
    return error;
  }
}

export const generateEligibleStudentsCertificate = async (
  sessionId: number,
  instanceId: number,
  studentIds: number[],
  completionCertificateGeneratedStatus: string
) => {
  try {
    const response = await api.put(
      config.trainingEndPoints.generateEligibleStudentsCertificate,
      {
        studentIds,
        completionCertificateGeneratedStatus,
      },
      {
        params: {
          trainingSessionId: sessionId,
          trainingInstanceId: instanceId,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error approving the feedback: ", error);
    return null;
  }
};

export const promoteUser = async (
  trainerId: number,
  trainingSessionId: number,
  currentRole: string
) => {
  try {
    const response = await api.put(
      `${config.trainingEndPoints.promoteUser}/${trainerId}`,
      { role: currentRole },
      {
        params: { trainingSessionId},
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error promoting user:', error);
    return error;
  }
};

export const getCountry = async (search: string) => {
  try {
    const response = await api.get(
      `${config.trainingEndPoints.organizations}/country`,
      {
        params: { search, pageSize: 200 },
      }
    );
    return response.data.data.countries;
  } catch (error) {
    console.error("Error fetching states:", error);
    return error;
  }
};

export const getRegion = async (countryCode: string, search: string) => {
  try {
    const response = await api.get(
      `${config.trainingEndPoints.organizations}/region?countryCode=${countryCode}`,
      {
        params: { search, pageSize: 50 },
      }
    );
    return response.data.data.regions;
  } catch (error) {
    console.error("Error fetching states:", error);
    return error;
  }
};

export const getState = async (regionCode: string, search:string) => {
  try {
    const response = await api.get(
      `${config.trainingEndPoints.organizations}/state?countryCode=${regionCode}`,
      {
        params: { search, pageSize: 50 },
      }
    );

    return response.data.data.states;
  } catch (error) {
    console.error("Error fetching states:", error);
    return error;
  }
};

export interface OrganisationListParams {
  type?: string;
  state?: number;
  country?: number;
  region?: number;
  sortBy?: string;
  orderBy?:string;
  pageNumber?: number;
  pageSize?: number;
  pageOrder?: "asc" | "desc";
  name?: string;
}

export interface TrainingSessionListParam {
  trainingType?: string;
  status?: string;
  sponsoringOrganizationID?: string;
  isHybrid?: boolean;
  search?: string;
}

export const getOrganisationList = async (params: OrganisationListParams) => {
  try {
    const response = await api.get(
      config.trainingEndPoints.organizations,
      { params }
    );
    return response.data;
  } catch (error) {
    return error
  }
};

export interface ConsentListApi {
  search?: string;
  status?: string;
  pageNumber?: number;
  pageSize?: number;
  pageOrder?: string;
}
export const fetchUserConsentRequests = async (params: ConsentListApi) => {
  try {
    const response = await api.get(config.userEndPoints.userConsentRequest, {
      params,
    });
    let consentData = response.data?.data?.userConsent || [];
    consentData = await Promise.all(
      consentData.map(async (consent: any) => ({
        ...consent,
        firstName: await CryptoMethods.ecbDecryptData(consent.firstName),
        lastName: await CryptoMethods.ecbDecryptData(consent.lastName),
        email: await CryptoMethods.ecbDecryptData(consent.email),
      }))
    );
    return {
      ...response.data,
      data: { ...response.data.data, userConsent: consentData },
    };
  } catch (error) {
    return error;
  }
};

export const createConsentRequest = async (payload: Record<string, any>) => {
  try {
    const response = await api.post(
      config.userEndPoints.consentRequest,
      payload,
      {
        headers: {
          "X-Api-Key": config.xApiKey,
        }
      }
    );
    return response.data.data.userConsent;
  } catch (error) {
    return error;
  }
};

export const uploadConsentDocument = async (
  file: File,
  documentType: string,
  role: string
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentType", documentType);
    formData.append("role", role);

    const response = await api.post(
      config.userEndPoints.uploadDocument,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Tenant-Id": "1",
          "X-Api-Key": config.xApiKey,
        },
      }
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export interface FeedbackDocParams {
  userId?: number;
  documentType?: string;
  pageNumber?: number;
  pageSize?: number;
  pageOrder?: string;
}
export const fetchFeedBackDocuments = async (
  params: FeedbackDocParams
) => {
  try {
    const url = `${config.userEndPoints.userDocuments}`;
    const response = await api.get(url, {
      params,
    });
    return response?.data;
  } catch (error) {
    return error;
  }
};

export const uploadFacilitatorFeedback = async (userId:any, documentType:string, file:File | string) => {
  try {
    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("documentType", documentType);
    formData.append("file", file);

    const response = await api.post(
      config.userEndPoints.uploadfeedback,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Tenant-Id": "1",
        },
      }
    )
    return response.data
  } catch (error) {
    return error
  }
}

export const fetchConsentRequestById = async (id: number) => {
  try {
    const response = await api.get(
      `${config.userEndPoints.userConsentRequest}/${id}`
    );
    const consent = response?.data?.data?.userConsent;
    consent.firstName = await CryptoMethods.ecbDecryptData(consent.firstName);
    consent.lastName = await CryptoMethods.ecbDecryptData(consent.lastName);
    consent.email = await CryptoMethods.ecbDecryptData(consent.email);
    return response?.data?.data?.userConsent;
  } catch (error) {
    return error;
  }
};

export const updateConsentStatus = async (id: number, isApproved: boolean) => {
  try {
    const response = await api.put(
      `${config.userEndPoints.userConsentRequestStatus}${id}`,
      { isConsentApproved: isApproved }
    );
    return response?.data;
  } catch (error) {
    return error;
  }
};

export const sendFeedbackReminder = async (instanceId: number) => {
  try {
    const response = await api.put(
      `${config.trainingEndPoints.feedBackReminder}/${instanceId}`
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export const converthtmltopdf = async (html: any) => {
  try {
    const formData = new FormData();
    formData.append("template", html);
    const response = await api.post(
      config.trainingEndPoints.converthtmltopdf,
      formData
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getCertificateById = async (id: number) => {
  try {
    const response = await api.get(
      `${config.trainingEndPoints.certificate}/${id}`
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export const updateCertificateTemplate = async (
  payload: Record<string, any>
) => {
  try {
    const response = await api.post(
      config.trainingEndPoints.certificate,
      payload
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export interface CertificateListParams {
  certificateType?: string;
  organisationId?: number;
  pageNumber?: number;
  pageSize?: number;
  name?: string
}

export const getCertificate = async (params: CertificateListParams) => {
  try {
    const response = await api.get(`${config.trainingEndPoints.certificate}`, {
      params,
    });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getOrganisationListExport = async (
  params: OrganisationListParams
) => {
  try {
    const response = await api.get(config.trainingEndPoints.orgExport, {
      params,
    });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getTrainingSessionListExport = async (
  params: TrainingSessionListParam
) => {
  try {
    const response = await api.get(
      config.trainingEndPoints.trainingSessionExport,
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    return error;
  }
};

export interface ConsentListParam {
  search?: string;
  status?: string;
}

export const fetchUserConsentListExport = async (params: ConsentListParam) => {
  try {
    const response = await api.get(config.userEndPoints.userConsentExport, {
      params,
    });
    let consentData = response.data?.data?.userConsent || [];
    consentData = await Promise.all(
      consentData.map(async (consent: any) => ({
        ...consent,
        firstName: await CryptoMethods.ecbDecryptData(consent.firstName),
        lastName: await CryptoMethods.ecbDecryptData(consent.lastName),
        email: await CryptoMethods.ecbDecryptData(consent.email),
      }))
    );
    return {
      ...response.data,
      data: { ...response.data.data, userConsent: consentData },
    };
  } catch (error) {
    return error;
  }
};

export const getStudentExportList = async (
  sessionId: number,
  instanceId?: number
) => {
  try {
    const params: Record<string, number> = { trainingSessionId: sessionId };
    if (instanceId) params.trainingInstanceId = instanceId;

    const response = await api.get(config.trainingEndPoints.studentExport, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error("Error in getStudentExportList:", error);
    throw error;
  }
};